import React from 'react';
import { cn } from '../../lib/media/badgeColor';
import { formatMonthYear, formatWeekday, parseISODate } from '../../lib/media/dates';
import type {
  ColorBy,
  EditablePostField,
  MediaFormat,
  MediaGoal,
  MediaPost,
} from '../../lib/media/types';
import { ExternalLink, Maximize2, Trash2 } from '../../components/media/MediaIcons';
import { PostedToggle } from '../../components/media/MediaShared';
import { Button } from '../../components/media/MediaUI';
import { InlineDate, InlineSelect, InlineText } from './InlineFields';

interface WeekGroup {
  id: string;
  label: string;
  hint: string | null;
  posts: MediaPost[];
}

/**
 * `week_no` is generated by the database, so it is the authority whenever
 * present. A row with a date but no computed week still gets a sensible bucket
 * from the date, and undated posts collect in one "Unscheduled" group rather than
 * disappearing.
 */
function groupByWeek(posts: MediaPost[]): WeekGroup[] {
  const groups = new Map<string, WeekGroup>();

  for (const post of posts) {
    const hasWeek = typeof post.week_no === 'number';
    const id = hasWeek
      ? `w-${post.week_no}`
      : post.post_date
        ? `d-${post.post_date.slice(0, 7)}`
        : 'unscheduled';

    if (!groups.has(id)) {
      groups.set(id, {
        id,
        label: hasWeek
          ? post.week_label?.trim() || `Week ${post.week_no}`
          : post.post_date
            ? formatMonthYear(parseISODate(post.post_date))
            : 'Unscheduled',
        hint: hasWeek && post.week_label?.trim() ? `Week ${post.week_no}` : null,
        posts: [],
      });
    }
    groups.get(id)!.posts.push(post);
  }

  // Map iteration preserves insertion order, and getPosts() orders by
  // post_date ASC NULLS LAST — so the groups come out chronologically with
  // "Unscheduled" last.
  return Array.from(groups.values());
}

const COLUMNS = [
  'Date',
  'Day',
  'Goal',
  'Format',
  'Objective',
  'Text on Visual / Video Script',
  'Caption',
  'CTA',
  'Media Link',
  'Reference',
  'Posted',
  '',
];

export const PostsListView: React.FC<{
  posts: MediaPost[];
  goals: MediaGoal[];
  formats: MediaFormat[];
  postedPendingId: string | null;
  colorBy: ColorBy;
  onSaveField: (postId: string, field: EditablePostField, value: string | null) => Promise<boolean>;
  onTogglePosted: (post: MediaPost) => void;
  onOpen: (post: MediaPost) => void;
  onDelete: (post: MediaPost) => void;
}> = ({
  posts,
  goals,
  formats,
  postedPendingId,
  colorBy,
  onSaveField,
  onTogglePosted,
  onOpen,
  onDelete,
}) => {
  const groups = groupByWeek(posts);
  const goalOptions = goals.map((g) => ({ key: g.key, label: g.label, color: g.color }));
  const formatOptions = formats.map((f) => ({ key: f.key, label: f.label, color: f.color }));

  return (
    <div className="overflow-hidden rounded-2xl border border-black/[0.07] bg-white">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1320px] border-collapse text-left">
          <thead className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm">
            <tr className="border-b border-black/[0.07]">
              {COLUMNS.map((label, i) => (
                <th
                  key={i}
                  scope="col"
                  className="whitespace-nowrap px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-black/40"
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {groups.map((group) => (
              <React.Fragment key={group.id}>
                <tr>
                  <th
                    scope="colgroup"
                    colSpan={COLUMNS.length}
                    className="border-y border-subtle bg-surface px-3 py-2 text-left"
                  >
                    <span className="inline-flex items-center gap-2">
                      <span className="text-[12px] font-semibold tracking-[-0.008em] text-black/70">
                        {group.label}
                      </span>
                      {group.hint && (
                        <span className="text-[11px] font-medium text-black/30">{group.hint}</span>
                      )}
                      <span className="rounded-full bg-black/[0.05] px-1.5 text-[10.5px] font-semibold tabular-nums text-black/45">
                        {group.posts.length}
                      </span>
                    </span>
                  </th>
                </tr>

                {group.posts.map((post) => {
                  const label = post.objective?.trim() || post.caption?.trim() || 'Untitled post';
                  return (
                    <tr
                      key={post.id}
                      className="group/row border-b border-black/[0.04] align-top transition-colors duration-150 last:border-b-0 hover:bg-black/[0.015]"
                    >
                      <td className="px-1 py-2">
                        <InlineDate
                          value={post.post_date}
                          ariaLabel="Post date"
                          onSave={(v) => onSaveField(post.id, 'post_date', v)}
                        />
                      </td>
                      <td className="whitespace-nowrap px-3 py-3.5 text-[12.5px] text-black/45">
                        {post.post_date ? formatWeekday(parseISODate(post.post_date)) : '—'}
                      </td>
                      <td className="px-2 py-2">
                        <InlineSelect
                          value={post.goal_key}
                          options={goalOptions}
                          placeholder="No goal"
                          ariaLabel="Post goal"
                          showDot={colorBy === 'goal'}
                          onSave={(v) => onSaveField(post.id, 'goal_key', v)}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <InlineSelect
                          value={post.format_key}
                          options={formatOptions}
                          placeholder="No format"
                          ariaLabel="Post format"
                          showDot={colorBy === 'format'}
                          onSave={(v) => onSaveField(post.id, 'format_key', v)}
                        />
                      </td>
                      <td className="min-w-[180px] px-1 py-2">
                        <InlineText
                          value={post.objective}
                          placeholder="Add objective"
                          ariaLabel="Objective"
                          onSave={(v) => onSaveField(post.id, 'objective', v)}
                        />
                      </td>
                      <td className="min-w-[220px] px-1 py-2">
                        <InlineText
                          multiline
                          value={post.visual_script}
                          placeholder="Add on-screen text or script"
                          ariaLabel="Text on visual or video script"
                          onSave={(v) => onSaveField(post.id, 'visual_script', v)}
                        />
                      </td>
                      <td className="min-w-[220px] px-1 py-2">
                        <InlineText
                          multiline
                          value={post.caption}
                          placeholder="Add caption"
                          ariaLabel="Caption"
                          onSave={(v) => onSaveField(post.id, 'caption', v)}
                        />
                      </td>
                      <td className="min-w-[130px] px-1 py-2">
                        <InlineText
                          value={post.cta}
                          placeholder="Add CTA"
                          ariaLabel="Call to action"
                          onSave={(v) => onSaveField(post.id, 'cta', v)}
                        />
                      </td>
                      <td className="min-w-[160px] px-1 py-2">
                        <div className="flex items-start gap-1">
                          <InlineText
                            value={post.media_link}
                            placeholder="Add link"
                            ariaLabel="Media link"
                            className="truncate"
                            onSave={(v) => onSaveField(post.id, 'media_link', v)}
                          />
                          {post.media_link && (
                            <a
                              href={post.media_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Open media link in a new tab"
                              className="mt-1.5 shrink-0 rounded-md p-1 text-black/30 transition-colors hover:bg-black/[0.05] hover:text-black/60"
                            >
                              <ExternalLink size={12} strokeWidth={1.75} />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="min-w-[160px] px-1 py-2">
                        <div className="flex items-start gap-1">
                          <InlineText
                            value={post.reference_url}
                            placeholder="Add reference"
                            ariaLabel="Reference link"
                            className="truncate"
                            onSave={(v) => onSaveField(post.id, 'reference_url', v)}
                          />
                          {post.reference_url && (
                            <a
                              href={post.reference_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Open the reference link in a new tab"
                              className="mt-1.5 shrink-0 rounded-md p-1 text-black/30 transition-colors hover:bg-[#6ea4e7]/[0.08] hover:text-[#6ea4e7]"
                            >
                              <ExternalLink size={12} strokeWidth={1.75} />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {/* Editable here — the admin owns this column. */}
                        <PostedToggle
                          posted={post.posted}
                          pending={postedPendingId === post.id}
                          onToggle={() => onTogglePosted(post)}
                          ariaLabel={`Mark ${label} as posted`}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div
                          className={cn(
                            'flex items-center justify-end gap-0.5 opacity-0 transition-opacity duration-150',
                            'focus-within:opacity-100 group-hover/row:opacity-100',
                          )}
                        >
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Open post details"
                            onClick={() => onOpen(post)}
                            className="text-black/30 hover:text-black/70"
                          >
                            <Maximize2 size={13} strokeWidth={1.75} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Delete ${label}`}
                            onClick={() => onDelete(post)}
                            className="text-black/30 hover:bg-[#d4183d]/10 hover:text-[#d4183d]"
                          >
                            <Trash2 size={13} strokeWidth={1.75} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
